<template>
    <!-- Visionneuse plein écran façon Slack : images, PDF, vidéos et sons ; navigation entre les fichiers de la liste -->
    <div class="ix-preview-host">
    <div class="ix-preview" v-if="current">
        <div class="ix-preview-header">
            <div class="ix-preview-file">
                <div class="ix-dm-ico" :class="type"><i class="far" :class="icon"></i></div>
                <div class="ix-preview-file-body">
                    <div class="ix-preview-name">{{ current.name }}.{{ current.extension }}</div>
                    <div class="ix-preview-meta">
                        <span v-if="current.user">{{ current.user.fullname }}</span>
                        <span v-if="current.updated_at"> · {{ current.updated_at | formatDate('DD/MM/YYYY à HH:mm') }}</span>
                        <span v-if="current.size"> · {{ current.size | formatFileSize(1) }}</span>
                    </div>
                </div>
            </div>
            <div class="ix-preview-tools">
                <template v-if="type == 'img' && !failed">
                    <button type="button" class="ix-preview-tool is-zoom" @click="zoomBy(-0.25)" title="Dézoomer"><i class="far fa-search-minus"></i></button>
                    <span class="ix-preview-zoom is-zoom" @click="zoom = 1" title="Ajuster à l'écran">{{ Math.round(zoom * 100) }} %</span>
                    <button type="button" class="ix-preview-tool is-zoom" @click="zoomBy(0.25)" title="Zoomer"><i class="far fa-search-plus"></i></button>
                    <span class="ix-preview-divider is-zoom"></span>
                </template>
                <a :href="current.download_path || current.document_path" class="ix-preview-tool" title="Télécharger"><i class="far fa-arrow-to-bottom"></i></a>
                <a :href="current.document_path" target="_blank" class="ix-preview-tool" title="Ouvrir dans un nouvel onglet"><i class="far fa-external-link"></i></a>
                <span class="ix-preview-divider"></span>
                <button type="button" class="ix-preview-tool" @click="close" title="Fermer (Échap)"><i class="far fa-times"></i></button>
            </div>
        </div>

        <div class="ix-preview-stage" @click.self="close" @touchstart.passive="onTouchStart" @touchend="onTouchEnd">
            <button type="button" class="ix-preview-nav previous" v-if="files.length > 1" @click="previous" title="Précédent (←)">
                <i class="far fa-chevron-left"></i>
            </button>

            <div class="ix-preview-image" v-if="type == 'img' && !failed" :class="{ 'is-zoomed': zoom > 1 }" @click.self="close">
                <img :src="current.document_path" :alt="current.name" :style="imageStyle" @click="zoom = zoom > 1 ? 1 : 2" @error="failed = true">
            </div>

            <iframe v-else-if="type == 'pdf'" :src="current.document_path" class="ix-preview-pdf" :title="current.name"></iframe>

            <video v-else-if="type == 'video' && !failed" :src="current.document_path" controls class="ix-preview-media" @error="failed = true"></video>

            <div v-else-if="type == 'audio' && !failed" class="ix-preview-card">
                <div class="ix-dm-ico audio large"><i class="far fa-file-audio"></i></div>
                <div class="ix-preview-card-name">{{ current.name }}.{{ current.extension }}</div>
                <audio :src="current.document_path" controls @error="failed = true"></audio>
            </div>

            <!-- Classeur : feuilles rendues par le serveur -->
            <div v-else-if="isSpreadsheet && (sheetLoading || sheets)" class="ix-preview-sheet">
                <div class="ix-preview-sheet-loading" v-if="sheetLoading"><i class="far fa-circle-notch fa-spin"></i> Préparation de l'aperçu…</div>
                <template v-else-if="sheet">
                    <div class="ix-preview-sheet-notice" v-if="sheet.truncated">
                        <i class="far fa-info-circle"></i> Aperçu limité aux {{ sheet.rows.length }} premières lignes et {{ sheet.columns.length }} premières colonnes
                        ({{ sheet.total_rows }} lignes au total). Téléchargez le fichier pour tout voir.
                    </div>
                    <div class="ix-preview-sheet-scroll">
                        <table class="ix-preview-grid">
                            <thead>
                                <tr>
                                    <th class="corner"></th>
                                    <th v-for="column in sheet.columns" :key="column.label" :style="column.width ? { minWidth: column.width + 'px', width: column.width + 'px' } : {}">{{ column.label }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, rowIndex) in sheet.rows" :key="rowIndex">
                                    <th>{{ rowIndex + 1 }}</th>
                                    <template v-for="(cell, cellIndex) in row">
                                        <td v-if="cell" :key="cellIndex" :colspan="cell.cs" :rowspan="cell.rs" :style="cellStyle(cell)">{{ cell.v }}</td>
                                    </template>
                                </tr>
                                <tr v-if="sheet.rows.length == 0"><td :colspan="sheet.columns.length + 1" class="text-center">Feuille vide</td></tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="ix-preview-sheet-tabs" v-if="sheets.length > 1">
                        <button type="button" v-for="(item, index) in sheets" :key="'sheet-' + index" :class="{ active: index == sheetIndex }" @click="sheetIndex = index">{{ item.name }}</button>
                    </div>
                </template>
            </div>

            <!-- Word (.docx) : converti en page lisible dans le navigateur -->
            <div v-else-if="isWord && (wordLoading || wordHtml !== null)" class="ix-preview-doc">
                <div class="ix-preview-sheet-loading" v-if="wordLoading"><i class="far fa-circle-notch fa-spin"></i> Préparation de l'aperçu…</div>
                <div class="ix-preview-doc-page" v-else>
                    <div class="ix-preview-doc-notice"><i class="far fa-info-circle"></i> Aperçu simplifié : la mise en page exacte (pages, en-têtes, pieds de page) peut différer de Word.</div>
                    <div class="ix-preview-doc-content" v-html="wordHtml"></div>
                </div>
            </div>

            <div v-else class="ix-preview-card">
                <div class="ix-dm-ico large" :class="type"><i class="far" :class="icon"></i></div>
                <div class="ix-preview-card-name">{{ current.name }}.{{ current.extension }}</div>
                <div class="ix-preview-card-text">{{ sheetError || wordError || unavailableMessage }}</div>
                <a :href="current.download_path || current.document_path" class="ix-dm-btn primary"><i class="far fa-arrow-to-bottom"></i> Télécharger</a>
            </div>

            <button type="button" class="ix-preview-nav next" v-if="files.length > 1" @click="next" title="Suivant (→)">
                <i class="far fa-chevron-right"></i>
            </button>
        </div>

        <div class="ix-preview-footer" v-if="files.length > 1">{{ index + 1 }} / {{ files.length }}</div>
    </div>
    </div>
</template>

<script>
import { fileType, fileIcon } from "./documentFileTypes"

// Classeurs prévisualisables par le serveur (SpreadsheetPreview)
const SPREADSHEET_EXTENSIONS = ['xlsx', 'xlsm', 'xls', 'ods', 'csv'];

// Convertisseur Word → HTML, chargé une seule fois et seulement quand un .docx est ouvert
const MAMMOTH_URL = '/js/mammoth.browser.min.js';
let mammothLoading = null;

function loadMammoth() {
    if (window.mammoth) return Promise.resolve(window.mammoth);
    if (!mammothLoading) {
        mammothLoading = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = MAMMOTH_URL;
            script.onload = () => resolve(window.mammoth);
            script.onerror = () => {
                mammothLoading = null;
                reject();
            };
            document.head.appendChild(script);
        });
    }
    return mammothLoading;
}

// Liens du document : seuls les liens web, mail et ancres sont conservés
function sanitizeWordHtml(html) {
    const container = document.createElement('div');
    container.innerHTML = html;
    container.querySelectorAll('script, iframe, object, embed').forEach(node => node.remove());
    container.querySelectorAll('*').forEach(node => {
        Array.from(node.attributes).forEach(attribute => {
            if (attribute.name.indexOf('on') === 0) node.removeAttribute(attribute.name);
        });
    });
    container.querySelectorAll('a[href]').forEach(link => {
        if (/^(https?:|mailto:|#)/i.test(link.getAttribute('href'))) {
            if (link.getAttribute('href').charAt(0) != '#') {
                link.setAttribute('target', '_blank');
                link.setAttribute('rel', 'noopener noreferrer');
            }
        } else {
            link.removeAttribute('href');
        }
    });
    return container.innerHTML;
}

export default {
    name: "DocumentPreview",
    props: {
        // Route des documents de l'API : l'aperçu d'un classeur est servi par {url}/{id}/preview
        url: String,
    },
    data() {
        return {
            files: [],
            index: 0,
            zoom: 1,
            failed: false,
            sheets: null,
            sheetIndex: 0,
            sheetLoading: false,
            sheetError: null,
            // Aperçus déjà chargés, par document
            sheetCache: {},
            wordHtml: null,
            wordLoading: false,
            wordError: null,
            wordCache: {},
        }
    },
    computed: {
        current() {
            return this.files[this.index] || null;
        },
        type() {
            return this.current ? fileType(this.current.extension) : 'other';
        },
        icon() {
            return this.current ? fileIcon(this.current.extension) : 'fa-file';
        },
        isSpreadsheet() {
            return this.current && this.url && this.current.id && SPREADSHEET_EXTENSIONS.includes((this.current.extension || '').toLowerCase());
        },
        isWord() {
            return this.current && (this.current.extension || '').toLowerCase() == 'docx';
        },
        unavailableMessage() {
            const extension = this.current ? (this.current.extension || '').toLowerCase() : '';
            return extension == 'doc'
                ? "Aperçu disponible uniquement pour les fichiers .docx (format Word actuel)"
                : 'Aperçu non disponible pour ce type de fichier';
        },
        sheet() {
            return this.sheets ? this.sheets[this.sheetIndex] : null;
        },
        imageStyle() {
            return this.zoom > 1 ? { maxWidth: 'none', maxHeight: 'none', width: (this.zoom * 100) + '%' } : {};
        },
    },
    methods: {
        open(document, documents) {
            this.files = (documents && documents.length ? documents : [document]).filter(item => !item.is_folder);
            this.index = Math.max(0, this.files.findIndex(item => item.id === document.id));
            this.reset();
            window.addEventListener('keydown', this.onKeydown);
            window.document.body.classList.add('ix-preview-open');
        },
        close() {
            this.files = [];
            window.removeEventListener('keydown', this.onKeydown);
            window.document.body.classList.remove('ix-preview-open');
        },
        reset() {
            this.zoom = 1;
            this.failed = false;
            this.sheets = null;
            this.sheetIndex = 0;
            this.sheetError = null;
            this.sheetLoading = false;
            this.wordHtml = null;
            this.wordError = null;
            this.wordLoading = false;
            if (this.isSpreadsheet) {
                this.loadSheets(this.current);
            } else if (this.isWord) {
                this.loadWord(this.current);
            }
        },
        loadWord(document) {
            if (this.wordCache[document.id]) {
                this.wordHtml = this.wordCache[document.id];
                return;
            }

            this.wordLoading = true;
            const isCurrent = () => this.current && this.current.id === document.id;

            // Lien de téléchargement : servi par l'application (même domaine), quel que soit le stockage
            Promise.all([
                loadMammoth(),
                fetch(document.download_path || document.document_path, { credentials: 'same-origin' }).then(response => {
                    if (!response.ok) throw new Error();
                    return response.arrayBuffer();
                }),
            ]).then(([mammoth, arrayBuffer]) => mammoth.convertToHtml({ arrayBuffer: arrayBuffer })).then(result => {
                if (!isCurrent()) return;
                const html = sanitizeWordHtml(result.value || '') || '<p class="text-muted">Document vide</p>';
                this.wordCache[document.id] = html;
                this.wordHtml = html;
                this.wordLoading = false;
            }).catch(() => {
                if (!isCurrent()) return;
                this.wordLoading = false;
                this.wordError = 'Impossible de lire ce fichier';
            });
        },
        loadSheets(document) {
            if (this.sheetCache[document.id]) {
                this.sheets = this.sheetCache[document.id];
                return;
            }

            this.sheetLoading = true;
            fetch(this.url + '/' + document.id + '/preview', {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => response.json().then((data) => ({ ok: response.ok, data }))).then(({ ok, data }) => {
                // Réponse d'un fichier précédent : on a déjà changé de fichier
                if (!this.current || this.current.id !== document.id) return;
                this.sheetLoading = false;
                if (ok && data.sheets && data.sheets.length) {
                    this.sheetCache[document.id] = data.sheets;
                    this.sheets = data.sheets;
                } else {
                    this.sheetError = data.message || "Impossible de lire ce fichier";
                }
            }).catch(() => {
                if (!this.current || this.current.id !== document.id) return;
                this.sheetLoading = false;
                this.sheetError = "Impossible de lire ce fichier";
            });
        },
        cellStyle(cell) {
            const style = {};
            if (cell.b) style.fontWeight = 600;
            if (cell.i) style.fontStyle = 'italic';
            if (cell.c) style.color = cell.c;
            if (cell.bg) style.background = cell.bg;
            if (cell.a) style.textAlign = cell.a;
            return style;
        },
        previous() {
            this.index = (this.index - 1 + this.files.length) % this.files.length;
            this.reset();
        },
        next() {
            this.index = (this.index + 1) % this.files.length;
            this.reset();
        },
        zoomBy(step) {
            this.zoom = Math.min(4, Math.max(1, this.zoom + step));
        },
        onKeydown(e) {
            if (e.key == 'Escape') this.close();
            else if (e.key == 'ArrowLeft' && this.files.length > 1) this.previous();
            else if (e.key == 'ArrowRight' && this.files.length > 1) this.next();
        },
        // Mobile : balayer vers la gauche ou la droite passe au fichier suivant ou précédent (sauf image zoomée, qui défile)
        onTouchStart(e) {
            const touch = e.changedTouches[0];
            this.touchStart = { x: touch.clientX, y: touch.clientY };
        },
        onTouchEnd(e) {
            if (!this.touchStart || this.files.length < 2 || this.zoom > 1) return;
            const touch = e.changedTouches[0];
            const dx = touch.clientX - this.touchStart.x;
            const dy = touch.clientY - this.touchStart.y;
            this.touchStart = null;
            if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
                dx < 0 ? this.next() : this.previous();
            }
        },
    },
    mounted() {
        // Au-dessus de toute la page (menus, modales, en-tête fixe)
        window.document.body.appendChild(this.$el);
    },
    beforeDestroy() {
        this.close();
        if (this.$el.parentNode) this.$el.parentNode.removeChild(this.$el);
    },
}
</script>
